import React, { useState, useEffect } from 'react';
import {
    AlertCircle,
    FileText,
    UserPlus,
    X,
    ChevronRight,
    Mail,
    Phone,
    Briefcase,
    User,
    CheckCircle,
    Building2,
    Handshake,
    ChevronDown
} from 'lucide-react';
import {
    useAddCompanyToEnterpriseMutation,
    useCreateOrderForEnterpriseMutation
} from '../../store/Company/companyApiSlice';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { convertLetterCase, formatDateToHumanReadable } from '../../utils/Helper';

const EnterpriseAddCompany = ({ getPurchaseDetails, EnterpriseButtonText, triggerPurchaseApi, setTriggerPurchaseApi, statusData, companyDetails, enterpriseData }) => {
    const navigate = useNavigate();
    const { token, userInfo } = useSelector((state) => state.auth);
    const [showModal, setShowModal] = useState(false);
    const [step, setStep] = useState('select'); // 'select', 'confirmation', 'action', 'form'
    const [selectedEnterprise, setSelectedEnterprise] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [addCompanyToEnterprise, { isLoading: isAddingEnterprise }] =
        useAddCompanyToEnterpriseMutation();
    const [createOrderForEnterprise, { isLoading: isCreatingEnterpriseOrder }] =
        useCreateOrderForEnterpriseMutation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        designation: ''
    });
    const [reportsUsed, setReportsUsed] = useState(7);
    const [submitted, setSubmitted] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const reportsLimit = 10;
    const reportsRemaining = reportsLimit - reportsUsed;
    const [reportRemainingMessage, setReportRemainingMessage] = useState("");
    const [trackedCompanyId, setTrackedCompanyId] = useState("");

    // make sure enterprises is always an array
    const enterprises = (enterpriseData || []).map((item, index) => ({
        id: index + 1,
        ent_id: item.id,
        name: item.name,
        companies: item.total_companies, // you used id -> companies previously
        color: 'bg-orange-500',
        enterprise_type: item.enterprise_type,
        enterprise_status: getPurchaseDetails?.status ? getPurchaseDetails.status.includes("ADDED") ? "Added" : getPurchaseDetails.status.includes("ORDERED") ? "Ordered" : "Invited" : ""
    }));

    useEffect(() => {
        // safety for SSR
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        if (showModal) {
            // lock scroll
            document.body.style.overflow = 'hidden';
            // prevent layout shift by reserving scrollbar space
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
        } else {
            // restore
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        // cleanup on unmount (also ensures restoration if effect re-runs)
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [showModal]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleContinueToActions = () => {
        setStep('action');
    };

    const navigateToBilling = ({ plan, orderDetails }) => {
        if (token != undefined) {
            navigate("/plans/paymentdetails", {
                state: {
                    planDetails: plan,
                    company_id: companyDetails?.companyID || companyDetails?.company?.win,
                    purchase_track_id: orderDetails?.purchase_track_id,
                    discount_code: orderDetails?.discount_code
                },
            });
        } else {
            localStorage.setItem("lastVisited", `/search/${companyDetails?.companyID || companyDetails?.company?.win}`);
            localStorage.setItem("slug", plan.slug);
            navigate("/login")
        }
    }

    const buyFullProfile = (orderDetails) => {
        const plan = {
            title: "Pay-as-you-go",
            mode: "one-time",
            slug: "pay-as-you-go",
            subsCharge: 79,
            talkToSales: true,
            Features: [
                "1 profile views for 1 month",
                "full profile access for 1 month.",
            ],
        };
        navigateToBilling({ plan, orderDetails })
    }

    const handleSubmit = async () => {
        if (!isFormValid) return;
        const payloadData = {
            enterprise_id: selectedEnterprise?.ent_id,
            company_id: companyDetails?.companyID || companyDetails?.company?.win,
            invite: selectedOption === 'report' ? false : true,
            tracked_company_id: trackedCompanyId
        };
        const prefixedObj2 = Object.fromEntries(
            Object.entries(formData).map(([key, value]) => [`ref_${key}`, value])
        );

        const merged = { ...payloadData, ...prefixedObj2 };

        const res = await createOrderForEnterprise({
            token,
            data: merged
        });

        setTriggerPurchaseApi(!triggerPurchaseApi);
        if (selectedOption === 'report' && res?.data.purchase_track_id) {
            // Redirect to Stripe payment
            // window.open('https://stripe.com/payment', '_blank');
            buyFullProfile(res.data)
            return;
        } else if (selectedOption !== 'invite') {
            window.location.reload();
        }
        setSubmitted(true);
        setTimeout(() => {
            setShowModal(false);
            setSubmitted(false);
            setStep('select');
            setSelectedEnterprise(null);
            setSelectedOption(null);
            setFormData({ name: '', email: '', phone: '', designation: '' });
        }, 2500);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setStep('select');
        setSelectedEnterprise(null);
        setSelectedOption(null);
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', designation: '' });
        window.location.reload();
    };

    const isFormValid =
        formData.name && formData.email && formData.phone && formData.designation;

    const handleEnterpriseSelect = async (enterprise) => {
        const payloadData = {
            enterprise_id: enterprise?.ent_id,
            company_id: companyDetails?.companyID || companyDetails?.company?.win,

            ...(getPurchaseDetails?.tracked_company_id && {
                tracked_company_id: getPurchaseDetails.tracked_company_id
            })
        };
        try {
            const res = await addCompanyToEnterprise({
                token,
                data: payloadData
            });
            // assume truthy response means success; adjust as per your API shape
            if (res?.data?.status === "success") {
                setTriggerPurchaseApi(!triggerPurchaseApi);
                setReportRemainingMessage(res?.data?.credit_remaing_message);
                setTrackedCompanyId(res?.data.tracked_company_id);
                setSelectedEnterprise(enterprise);
                setShowDropdown(false);
                setStep('confirmation');
            }
        } catch (err) {
            console.error('Failed to add company to enterprise', err);
            // you may want to show toast / error UI here
        }
    };

    // helper to render either handshake or building icon (white icon inside colored bg)
    const EnterpriseIcon = ({ enterprise, size = 24 }) => {
        return enterprise?.enterprise_type === 'PARTNER_PROGRAM' ? (
            <Handshake className="text-white" size={size} />
        ) : (
            <Building2 className="text-white" size={size} />
        );
    };

    return (
        <div className="text-left">
            <button
                onClick={(EnterpriseButtonText === "Ordered" || EnterpriseButtonText === "Order Created") ? null : () => setShowModal(true)}
                disabled={(EnterpriseButtonText === "Ordered" || EnterpriseButtonText === "Order Created")}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
                {EnterpriseButtonText === "Add to Enterprise" && <span className="text-lg">+</span>}
                {EnterpriseButtonText === "Ordered" ? companyDetails?.companyID.startsWith("TRX") ? "Added to Enterprise" :`Purchased • Valid till ${formatDateToHumanReadable(getPurchaseDetails?.expireDate, 'dd MMM yyyy')}` : EnterpriseButtonText === "Order Created" ? "Order in Progress": EnterpriseButtonText}
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {step === 'select' && 'Select Enterprise Solution'}
                                {step === 'confirmation' && 'Company Added Successfully'}
                                {step === 'action' && 'Choose Next Action'}
                                {step === 'form' && 'Reference Details'}
                            </h2>
                            <button
                                onClick={handleModalClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div
                            className="overflow-y-auto"
                            style={{
                                // Reserve space for header/footer: adjust 64px if your header/footer heights differ
                                maxHeight: 'calc(90vh - 64px)',
                                WebkitOverflowScrolling: 'touch', // smooth mobile scrolling
                                scrollbarGutter: 'stable' // avoid layout shift when scrollbar appears (modern browsers)
                            }}
                        >

                            {/* Step 1: Select Enterprise */}
                            {step === 'select' && (
                                <div className="p-6">
                                    <p className="text-gray-600 mb-6">{`Select which enterprise solution you want to add ${companyDetails?.company?.name ||
                                        companyDetails?.registered_company_name} to:`}</p>

                                    <div className="space-y-3 flex justify-between items-center">
                                        {enterprises.map((enterprise) => (
                                            <button
                                                key={enterprise.id}
                                                onClick={() => handleEnterpriseSelect(enterprise)}
                                                className="w-full border-2 border-gray-200 hover:border-green-500 rounded-xl p-4 text-left transition-all hover:shadow-md group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div
                                                        className={`w-12 h-12 ${enterprise.color} rounded-lg flex items-center justify-center`}
                                                    >
                                                        <EnterpriseIcon enterprise={enterprise} size={24} />
                                                    </div>

                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                            {enterprise.name}
                                                        </h3>
                                                        <p className="text-sm text-gray-500">
                                                            {enterprise.companies} companies
                                                        </p>
                                                    </div>

                                                    <p className="font-bold text-green-500 text-sm">{enterprise.enterprise_status}</p>

                                                    <ChevronRight
                                                        className="text-gray-400 group-hover:text-green-500 ml-auto"
                                                        size={24}
                                                    />
                                                </div>
                                            </button>
                                        ))}

                                        {enterprises.length === 0 && (
                                            <div className="text-sm text-gray-500">No enterprise solutions available.</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Confirmation */}
                            {step === 'confirmation' && selectedEnterprise && (
                                <div className="p-6">
                                    <div className="text-center mb-6">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle className="text-green-600" size={32} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Company Added!</h3>
                                        <p className="text-gray-600">{`${companyDetails?.company?.name || companyDetails?.registered_company_name} has been successfully added to`}</p>

                                        <div
                                            className={`inline-flex items-center gap-2 mt-3 px-4 py-2 ${selectedEnterprise.color} bg-opacity-10 rounded-lg`}
                                        >
                                            <div className="flex items-center justify-center">
                                                {/* use handshake/building depending on enterprise type */}
                                                <div className="">
                                                    {selectedEnterprise.enterprise_type === 'PARTNER_PROGRAM' ? (
                                                        <Handshake className="text-gray-900 text-[20px]" size={20} />
                                                    ) : (
                                                        <Building2 className="text-gray-900 text-[20px]" size={20} />
                                                    )}
                                                </div>
                                            </div>

                                            <span className="font-semibold text-gray-900">{selectedEnterprise.name}</span>
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <p className="text-blue-900 text-sm">
                                            You can now order a report or invite a buyer to access this company profile.
                                        </p>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleModalClose}
                                            className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            Done
                                        </button>
                                        {(statusData !== "verified" && statusData !== "claimed") && <button
                                            onClick={handleContinueToActions}
                                            className="flex-1 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium"
                                        >
                                            Continue
                                        </button>}
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Choose Action */}
                            {step === 'action' && !selectedOption && selectedEnterprise && (
                                <div className="p-6">
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-3">
                                        <div
                                            className={`w-10 h-10 ${selectedEnterprise.color} rounded-lg flex items-center justify-center flex-shrink-0`}
                                        >
                                            {/* show handshake if partner program here as well */}
                                            {selectedEnterprise.enterprise_type === 'PARTNER_PROGRAM' ? (
                                                <Handshake className="text-white" size={20} />
                                            ) : (
                                                <Building2 className="text-white" size={20} />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Added to</p>
                                            <p className="font-semibold text-gray-900">{selectedEnterprise.name}</p>
                                        </div>
                                    </div>

                                    <p className="text-gray-600 mb-6">What would you like to do next?</p>

                                    <div className="space-y-4">
                                        {/* Order Report Option */}
                                        <button
                                            onClick={() => {
                                                setSelectedOption('report');
                                                setStep('form');
                                            }}
                                            className="w-full border-2 border-gray-200 hover:border-orange-500 rounded-xl p-6 text-left transition-all hover:shadow-md group"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="w-[4rem] h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                                                    <FileText className="text-orange-600 group-hover:text-white" size={24} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">Order a Report</h3>
                                                        <ChevronRight className="text-gray-400 group-hover:text-orange-500" size={20} />
                                                    </div>
                                                    <p className="text-gray-600 mb-3">
                                                        Request a detailed company report. Our team will create a comprehensive analysis and send it to you.
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                        {reportRemainingMessage ? (
                                                            <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                                                {reportRemainingMessage}
                                                                {/* {reportsRemaining} free report{reportsRemaining !== 1 ? 's' : ''} remaining this month */}
                                                            </div>
                                                        ) : (
                                                            <div className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                                                                Payment required - Limit reached
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>

                                        {/* Invite Buyer Option */}
                                        <button
                                            onClick={() => {
                                                setSelectedOption('invite');
                                                setStep('form');
                                            }}
                                            className="w-full border-2 border-gray-200 hover:border-orange-500 rounded-xl p-6 text-left transition-all hover:shadow-md group"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="w-[4rem] h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                                                    <UserPlus className="text-orange-600 group-hover:text-white" size={24} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">Invite the Buyer</h3>
                                                        <ChevronRight className="text-gray-400 group-hover:text-orange-500" size={20} />
                                                    </div>
                                                    <p className="text-gray-600 mb-3">
                                                        Send an automated email invitation to the buyer reference. They'll receive access to view the profile.
                                                    </p>
                                                    <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium inline-block">
                                                        Instant access via email
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    <div className="mt-6 pt-6 border-t">
                                        <button
                                            onClick={handleModalClose}
                                            className="w-full px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            Skip for Now
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Form */}
                            {step === 'form' && selectedOption && !submitted && selectedEnterprise && (
                                <div className="p-6">
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-3">
                                        <div
                                            className={`w-10 h-10 ${selectedEnterprise.color} rounded-lg flex items-center justify-center flex-shrink-0`}
                                        >
                                            {selectedEnterprise.enterprise_type === 'PARTNER_PROGRAM' ? (
                                                <Handshake className="text-white" size={20} />
                                            ) : (
                                                <Building2 className="text-white" size={20} />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Enterprise Solution</p>
                                            <p className="font-semibold text-gray-900">{selectedEnterprise.name}</p>
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div
                                                className={`w-10 h-10 ${selectedOption === 'report' ? 'bg-orange-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}
                                            >
                                                {selectedOption === 'report' ? (
                                                    <FileText className={selectedOption === 'report' ? 'text-orange-600' : 'text-blue-600'} size={20} />
                                                ) : (
                                                    <UserPlus className="text-blue-600" size={20} />
                                                )}
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {selectedOption === 'report' ? 'Order Report' : 'Invite Buyer'} - Reference Details
                                            </h3>
                                        </div>
                                        <p className="text-gray-600 text-sm ml-13">
                                            {selectedOption === 'report'
                                                ? 'Provide reference details for the company report request.'
                                                : 'Provide buyer reference details to send the invitation email.'}
                                        </p>
                                    </div>

                                    {reportsRemaining <= 0 && selectedOption === 'report' && (
                                        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
                                            <AlertCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                                            <div>
                                                <p className="text-orange-900 font-medium mb-1">Monthly limit reached</p>
                                                <p className="text-orange-700 text-sm">
                                                    You've used all {reportsLimit} free reports this month. Complete payment to proceed with this order.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                <User size={16} className="text-gray-400" />
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                                placeholder="Enter reference's full name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                <Mail size={16} className="text-gray-400" />
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                                placeholder="reference@company.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                <Phone size={16} className="text-gray-400" />
                                                Contact Number *
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                                placeholder="+971 XX XXX XXXX"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                <Briefcase size={16} className="text-gray-400" />
                                                Designation *
                                            </label>
                                            <input
                                                type="text"
                                                name="designation"
                                                value={formData.designation}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                                placeholder="e.g., Procurement Manager"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3 mt-6 pt-6 border-t">
                                        <button
                                            onClick={() => {
                                                setSelectedOption(null);
                                                setStep('action');
                                            }}
                                            className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={!isFormValid}
                                            className={`flex-1 px-6 py-2.5 rounded-lg font-medium transition-colors ${isFormValid
                                                ? selectedOption === 'report'
                                                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                        >
                                            {selectedOption === 'report'
                                                ? reportsRemaining > 0
                                                    ? 'Submit Report Request'
                                                    : 'Proceed to Payment'
                                                : 'Send Invitation'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Success State */}
                            {submitted && (
                                <div className="p-8 text-center">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle className="text-green-600" size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Success!</h3>
                                    <p className="text-gray-600 mb-2">
                                        {selectedOption === 'report'
                                            ? 'Your report request has been submitted. Our team will process it shortly.'
                                            : 'Invitation email has been sent successfully to the buyer reference.'}
                                    </p>
                                    <p className="text-sm text-gray-500">For {selectedEnterprise?.name}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnterpriseAddCompany;
